import { type AppState, Auth0Provider } from "@auth0/auth0-react";
import mixpanel from "mixpanel-browser";
import rg4js from "raygun4js";
import React from "react";
import { HashRouter } from "react-router-dom";
import { Toaster } from "sonner";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar/Navbar";
import { ProvideAuth } from "@/hooks/useAuth";
import "./main.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { PopupWindow } from "@/components/ExternalPartner/AuthPopup/PopupWindow";
import Fallback from "@/components/Fallback/Fallback";
import { ApiProvider, useApiContext } from "@/contexts/ApiContext";
import { ConfigProvider } from "@/contexts/ConfigContext";
import useMixpanel from "@/hooks/useMixpanel";
import { id, readParams } from "@/lib/utils";
import App from "./App";
import { TooltipProvider } from "./components/ui/tooltip";
import { queryClient } from "./lib/queryClient";
import { isObject } from "./lib/type-guards";

// Wrapper component that only renders ProvideAuth when API context is ready.
// Fixes a race condition where ProvideAuth is rendered before the API context is ready.
// TODO: Remove ProvideAuth entirely and use react-query in components instead.
function AuthWrapper({ children }: { children: React.ReactNode }) {
  const apiContext = useApiContext();

  // Don't render ProvideAuth if API context is not available
  if (!apiContext) {
    return null;
  }

  return <ProvideAuth>{children}</ProvideAuth>;
}

const RAYGUN_APIKEY: string = import.meta.env.VITE_RAYGUN_API_KEY;
const MIXPANEL_APIKEY = import.meta.env.VITE_MIXPANEL_APIKEY;
const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT;
const API_URL: string = import.meta.env.VITE_API_URL;
const AUTH0_DOMAIN: string = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID: string = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE: string = import.meta.env.VITE_AUTH0_AUDIENCE;
const APP_VERSION: string = import.meta.env.VITE_VERSION;

if (RAYGUN_APIKEY) {
  rg4js("enableCrashReporting", true);
  // useConfig()[2]() is the getEnvironment function
  if (id(ENVIRONMENT) === "Production") {
    rg4js("enablePulse", true);
  }
  rg4js("apiKey", RAYGUN_APIKEY);
  rg4js("options", {
    disableAnonymousUserTracking: true,
    apiEndpoint: `${API_URL}`,
  });
  rg4js("setVersion", APP_VERSION.replace("v", ""));
}

if (MIXPANEL_APIKEY) {
  mixpanel.init(MIXPANEL_APIKEY, {
    debug: true,
    ignore_dnt: true,
    api_host: `${API_URL}`,
  });
}

const handleRedirectCallback = async (appState?: AppState | undefined) => {
  moveQueryParamsToSession(window.location);
  window.history.replaceState(
    {},
    document.title,
    appState?.returnTo || window.location.pathname,
  );
  useMixpanel()[0]("Login");
};

const moveQueryParamsToSession = (location: Location) => {
  const params = new URLSearchParams(location.search);
  params.forEach((value, key) => {
    if (key !== "code" && key !== "state") {
      sessionStorage.setItem(key, value);
    }
  });
};

// Need to intercept auth calls from external partners and handle them as part of the external auth popup
// any other requests should just fall through to the react router which will handle "normal" auth
const params =
  window.location.search.includes("state") && readParams(window.location.href);
const state = params ? params.get("state") : false;
let decodedState: unknown;
if (state) {
  try {
    decodedState = JSON.parse(atob(state));
  } catch {}
}
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_ENABLE_MSW !== "true") {
    return;
  }
  const { worker } = await import("./test-mocks/browser");
  await worker.start({ onUnhandledRequest: "bypass" });
}

if (isObject(decodedState) && "partner" in decodedState) {
  PopupWindow.notifyOpener(window.location.href, false);
} else {
  enableMocking().then(() => {
    createRoot(document.getElementById("root") as HTMLElement).render(
      <React.StrictMode>
        <TooltipProvider delayDuration={300}>
          <QueryClientProvider client={queryClient}>
            <HashRouter>
              <Auth0Provider
                domain={AUTH0_DOMAIN}
                clientId={AUTH0_CLIENT_ID}
                authorizationParams={{
                  redirect_uri: window.location.origin,
                  audience: AUTH0_AUDIENCE,
                }}
                onRedirectCallback={handleRedirectCallback}
              >
                <ApiProvider>
                  <AuthWrapper>
                    <ConfigProvider>
                      <ErrorBoundary FallbackComponent={Fallback}>
                        <Navbar />
                        <main id="main-page-wrapper">
                          <App />
                          <Toaster
                            position="top-right"
                            richColors
                            style={
                              {
                                "--width": "800px", // container width
                              } as React.CSSProperties
                            }
                          />
                        </main>
                        <Footer />
                      </ErrorBoundary>
                    </ConfigProvider>
                  </AuthWrapper>
                </ApiProvider>
              </Auth0Provider>
            </HashRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </TooltipProvider>
      </React.StrictMode>,
    );
  });
}
